import React, { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { track } from "../utils/analytics";
import AppHotDeals from "../components/home/appHotDeals";
import PrivacyPolicy from "../components/common/privacy-policy";
import TermsOfService from "../components/common/terms-service";
import AboutUs from "../components/home/aboutUs";
import InvestorLogin from "../views/investorLogin";
import InvestorHome from "../views/investorHome";
import KycOnboarding from "../views/kycOnboarding";
import AdminLogin from "../views/adminLogin";
import AdminDashboard from "../views/adminDashboard";
import JoinInvestment from "../views/joinInvestment";
import ContactUs from "../views/contactUs";
import ShopPage from "../views/shopPage";
import BoutiquePage from "../views/boutiquePage";
import ProductDetailPage from "../views/productDetailPage";
import JourneyDetail from "../views/journeyDetail";
import NotFound from "../views/notFound";
import RegisterPage from "../views/register";
import VerifyEmailPage from "../views/verifyEmail";
import ForgotPasswordPage from "../views/forgotPassword";
import ResetPasswordPage from "../views/resetPassword";
import AuthCallbackPage from "../views/authCallback";
// New public pages
import LandingPage from "../views/landingPage";
import AfricaPage from "../views/africaPage";
import ChinaPage from "../views/chinaPage";
import GlobePage from "../views/globePage";
import HomePage from "../views/homePage";
import JourneysPage from "../views/journeysPage";
import { getAdminToken, getSessionToken } from "../utils/auth";

const PageTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    track('page-view', { path: location.pathname });
  }, [location.pathname]);
  return null;
};

const InvestorProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const token = getSessionToken();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AdminProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const token = getAdminToken();
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

const AppRouter = () => {
  return (
    <Router>
      <PageTracker />
      <Routes>
        {/* ── Public landing & regional pages ── */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/africa" element={<AfricaPage />} />
        <Route path="/china" element={<ChinaPage />} />
        <Route path="/globe" element={<GlobePage />} />
        <Route path="/journeys" element={<JourneysPage />} />

        {/* ── Auth & investor protected ── */}
        <Route path="/login" element={<InvestorLogin />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/onboarding"
          element={
            <InvestorProtectedRoute>
              <KycOnboarding />
            </InvestorProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <InvestorProtectedRoute>
              <InvestorHome />
            </InvestorProtectedRoute>
          }
        />

        {/* ── Admin ── */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />

        {/* ── Shop & journeys detail ── */}
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/boutique/:id" element={<BoutiquePage />} />
        <Route path="/shop/boutique/:boutiqueId/product/:productId" element={<ProductDetailPage />} />
        <Route path="/journeys/:id" element={<JourneyDetail />} />
        <Route path="/join/:investmentId" element={<JoinInvestment />} />

        {/* ── Misc public pages ── */}
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/hotDeals" element={<AppHotDeals />} />
        <Route path="/aboutUs" element={<AboutUs />} />

        {/* Legacy redirects */}
        <Route path="/landing" element={<Navigate to="/" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
